import type { ContactRole, ContactRoles, UpdatedContactRoles } from "../organization.service";

export type OrganizationProfileMemberRoleRemoval = {
    contId: number;
    roleId: number;
};

export function isFullContactRemoval(entry: ContactRole | ContactRoles): entry is ContactRoles {
    if ((entry as ContactRole).role) {
        return false;
    }
    return Array.isArray((entry as ContactRoles).roles);
}

export function partitionMemberRemovals(removed: (ContactRole | ContactRoles)[]): {
    removed: OrganizationProfileMemberRoleRemoval[];
    removedContacts: number[];
} {
    const roleRemovals: OrganizationProfileMemberRoleRemoval[] = [];
    const removedContacts: number[] = [];

    for (const entry of removed) {
        if (!entry) {
            continue;
        }
        if (isFullContactRemoval(entry)) {
            removedContacts.push(entry.itemId);
            continue;
        }
        const roleId = entry.role?.itemId;
        if (roleId == null) {
            continue;
        }
        roleRemovals.push({ contId: entry.itemId, roleId });
    }

    return { removed: roleRemovals, removedContacts };
}

/** Contacts who will have zero org roles after applying roster changes. */
export function contactsFullyRemovedFromRoster(currentMembers: ContactRole[], changes: UpdatedContactRoles): number[] {
    const rolesByContact = new Map<number, Set<number>>();
    for (const member of currentMembers) {
        const roleIds = rolesByContact.get(member.itemId) ?? new Set<number>();
        if (member.role?.itemId != null) {
            roleIds.add(member.role.itemId);
        }
        rolesByContact.set(member.itemId, roleIds);
    }
    const beforeContactIds = new Set(currentMembers.map((member) => member.itemId));

    for (const added of changes.added) {
        const roleIds = rolesByContact.get(added.itemId) ?? new Set<number>();
        if (added.role?.itemId != null) {
            roleIds.add(added.role.itemId);
        }
        rolesByContact.set(added.itemId, roleIds);
    }
    for (const updated of changes.updated) {
        const roleIds = rolesByContact.get(updated.itemId) ?? new Set<number>();
        if (updated.role?.itemId != null) {
            roleIds.add(updated.role.itemId);
        }
        rolesByContact.set(updated.itemId, roleIds);
    }
    for (const removed of changes.removed) {
        if (isFullContactRemoval(removed)) {
            rolesByContact.delete(removed.itemId);
            continue;
        }
        const roleIds = rolesByContact.get(removed.itemId);
        if (!roleIds) {
            continue;
        }
        if (removed.role?.itemId != null) {
            roleIds.delete(removed.role.itemId);
        }
        if (roleIds.size === 0) {
            rolesByContact.delete(removed.itemId);
        }
    }

    return [...beforeContactIds].filter((contactId) => !rolesByContact.has(contactId));
}
